import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Dictionary } from 'lodash';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { Dvir } from 'src/app/models/dvir';
import { DvirService } from '../dvir/dvir.service';
import { TelematicDriverService } from 'src/app/shared/services/telematic-driver.service';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';

export interface DvirDetailDialogData {
  dvirId: string;
}

// The DVIR quick view: the inspection behind a piece of evidence, without leaving the
// screen that raised it. The routed DVIR page stays the full record; this shows what a
// reader needs to judge the defect — who reported it, when, and where the repair stands.
@Component({
  selector: 'app-dvir-detail-dialog',
  templateUrl: './dvir-detail-dialog.component.html',
  styleUrl: './dvir-detail-dialog.component.scss',
  standalone: true,
  imports: [CommonModule, RouterModule, AngularMaterialModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DvirDetailDialogComponent implements OnInit, OnDestroy {
  loading = false;
  error = false;
  dvir: Dvir | null = null;
  drivers: Dictionary<string> = {};

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DvirDetailDialogData,
    private readonly dialogRef: MatDialogRef<DvirDetailDialogComponent>,
    private readonly dvirService: DvirService,
    private readonly telematicDriverService: TelematicDriverService,
    private readonly spaRouting: SpaRoutingService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.data?.dvirId) return;
    this.loading = true;
    this.dvirService.getDvirById(this.data.dvirId)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => { this.loading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: dvir => {
          this.dvir = dvir;
          this.resolveDrivers(dvir);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = true;
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Driver values are telematic ids, not names. The same resolution the DVIR page uses.
  private resolveDrivers(dvir: Dvir): void {
    const ids = new Set<string>();
    if (dvir?.driver) ids.add(dvir.driver);
    if (dvir?.certifiedBy) ids.add(dvir.certifiedBy);
    for (const defect of dvir?.defectList ?? []) {
      if (defect?.repairedBy) ids.add(defect.repairedBy);
    }
    if (!ids.size) return;
    this.telematicDriverService.getDrivers([...ids], dvir.telematicProvider)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(dict => {
        this.drivers = dict ?? {};
        this.cdr.markForCheck();
      });
  }

  driverName(driverId: string): string {
    if (!driverId) return '';
    return this.drivers[this.telematicDriverService.driverKey(driverId, this.dvir?.telematicProvider)]
      ?? driverId;
  }

  get fullDvirLink(): unknown[] {
    return this.dvir?.id ? this.spaRouting.dvirLink(this.dvir.id) : [];
  }

  // The link routes on its own. This only clears the dialog out of the way, and only for
  // a plain click: a modified click opens the record in a new tab, so the reader has not
  // left this screen and the dialog they were reading must stay.
  onOpenFullDvir(event: MouseEvent): void {
    const opensElsewhere =
      event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0;
    if (opensElsewhere) return;
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
